import { createSlice } from '@reduxjs/toolkit';

const getInitialState = () => {
  try {
    const onBoardingCompleted = localStorage.getItem('on_boarding_completed');
    const role = localStorage.getItem('userRole') || '';
    const viewAs = localStorage.getItem('supervisorViewAsRole') || 'Patient';
    const medQ = localStorage.getItem('medical_questionnaire_completed');
    const nFilledRaw = localStorage.getItem('number_of_questions_filled');
    const nFilled =
      nFilledRaw != null && nFilledRaw !== ''
        ? Number(nFilledRaw)
        : null;
    const pctRaw = localStorage.getItem('questionnaire_completion_percentage');
    const pct =
      pctRaw != null && pctRaw !== '' && Number.isFinite(Number(pctRaw))
        ? Math.min(100, Math.max(0, Math.round(Number(pctRaw))))
        : null;
    const sectionsCompletedRaw =
      localStorage.getItem('section_completed') ??
      localStorage.getItem('sections_completed');
    const sectionsCompleted =
      sectionsCompletedRaw != null &&
      sectionsCompletedRaw !== '' &&
      Number.isFinite(Number(sectionsCompletedRaw))
        ? Math.max(0, Math.round(Number(sectionsCompletedRaw)))
        : null;
    const totalSectionsRaw = localStorage.getItem('total_sections');
    const totalSectionsFromStorage =
      totalSectionsRaw != null &&
      totalSectionsRaw !== '' &&
      Number.isFinite(Number(totalSectionsRaw))
        ? Math.max(0, Math.round(Number(totalSectionsRaw)))
        : null;
    const sectionProgressRaw = localStorage.getItem('section_progress');
    const sectionProgress =
      sectionProgressRaw != null &&
      sectionProgressRaw !== '' &&
      Number.isFinite(Number(sectionProgressRaw))
        ? Math.min(100, Math.max(0, Math.round(Number(sectionProgressRaw))))
        : null;
    return {
      fullName: localStorage.getItem('userFullName') || '',
      email: localStorage.getItem('userEmail') || '',
      gender: localStorage.getItem('userGender') || '',
      dateOfBirth: localStorage.getItem('userDOB') || null,
      role,
      organization: localStorage.getItem('userOrganization') || 'Biowell',
      // Only used when role === 'Supervisor': 'Patient' | 'Doctor/Nurse'
      viewAsRole: role === 'Supervisor' ? viewAs : null,
      on_boarding_completed: onBoardingCompleted !== null ? onBoardingCompleted === 'true' : null,
      on_boarding_step: localStorage.getItem('on_boarding_step') || '',
      must_change_password: localStorage.getItem('must_change_password') === 'true',
      show_password_step: localStorage.getItem('show_password_step') === 'true',
      // Unset storage means not completed yet (matches backend default false).
      medical_questionnaire_completed: medQ === null || medQ === '' ? false : medQ === 'true',
      number_of_questions_filled:
        nFilled !== null && Number.isFinite(nFilled) ? nFilled : null,
      questionnaire_completion_percentage: pct,
      totalSections: totalSectionsFromStorage,
      totalCompletedSections: null,
      totalIncompleteSections: null,
      completedSectionsPercentage: null,
      section_progress: sectionProgress,
      sections_completed: sectionsCompleted,
      overallStatus: null,
    };
  } catch (e) {
    return {
      fullName: '',
      email: '',
      gender: '',
      dateOfBirth: null,
      role: '',
      organization: 'Biowell',
      viewAsRole: null,
      on_boarding_completed: null,
      on_boarding_step: '',
      must_change_password: false,
      show_password_step: false,
      medical_questionnaire_completed: false,
      number_of_questions_filled: null,
      questionnaire_completion_percentage: null,
      totalSections: null,
      totalCompletedSections: null,
      totalIncompleteSections: null,
      completedSectionsPercentage: null,
      section_progress: null,
      sections_completed: null,
      overallStatus: null,
    };
  }
};

const initialState = getInitialState();

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setProfile(state, action) {
      const {
        fullName,
        email,
        gender,
        dateOfBirth,
        role,
        organization,
        on_boarding_completed,
        on_boarding_step,
        must_change_password,
        show_password_step,
        medical_questionnaire_completed,
        number_of_questions_filled,
        questionnaire_completion_percentage,
        totalSections,
        totalCompletedSections,
        totalIncompleteSections,
        completedSectionsPercentage,
        section_progress,
        sections_completed,
        overallStatus,
      } =
        action.payload || {};
      if (typeof fullName === 'string') {
        state.fullName = fullName;
        try {
          localStorage.setItem('userFullName', fullName);
        } catch (e) {
          // ignore localStorage errors
        }
      }
      if (typeof email === 'string') {
        state.email = email;
        try {
          localStorage.setItem('userEmail', email);
        } catch (e) {
          // ignore localStorage errors
        }
      }
      if (typeof gender === 'string') {
        state.gender = gender;
        try {
          localStorage.setItem('userGender', gender);
        } catch (e) {
          // ignore localStorage errors
        }
      }
      if (typeof dateOfBirth === 'string' || dateOfBirth === null) {
        state.dateOfBirth = dateOfBirth;
        try {
          if (dateOfBirth) {
            localStorage.setItem('userDOB', dateOfBirth);
          } else {
            localStorage.removeItem('userDOB');
          }
        } catch (e) {
          // ignore localStorage errors
        }
      }
      if (typeof role === 'string') {
        state.role = role;
        try {
          localStorage.setItem('userRole', role);
        } catch (e) {
          // ignore localStorage errors
        }
        if (role === 'Supervisor') {
          const viewAs = localStorage.getItem('supervisorViewAsRole') || 'Patient';
          state.viewAsRole = viewAs === 'Doctor/Nurse' ? 'Doctor/Nurse' : 'Patient';
          try {
            localStorage.setItem('supervisorViewAsRole', state.viewAsRole);
          } catch (e) {
            // ignore
          }
        } else {
          state.viewAsRole = null;
          try {
            localStorage.removeItem('supervisorViewAsRole');
          } catch (e) {
            // ignore
          }
        }
      }
      if (typeof organization === 'string') {
        state.organization = organization;
        try {
          localStorage.setItem('userOrganization', organization);
        } catch (e) {
          // ignore localStorage errors
        }
      }
      if (typeof on_boarding_completed === 'boolean') {
        state.on_boarding_completed = on_boarding_completed;
        try {
          localStorage.setItem('on_boarding_completed', String(on_boarding_completed));
        } catch (e) {
          // ignore
        }
        if (on_boarding_completed === true) {
          state.show_password_step = false;
          try {
            localStorage.setItem('show_password_step', 'false');
          } catch (err) {
            // ignore
          }
        }
      }
      if (typeof on_boarding_step === 'string') {
        state.on_boarding_step = on_boarding_step;
        try {
          localStorage.setItem('on_boarding_step', on_boarding_step);
        } catch (e) {
          // ignore
        }
      }
      if (typeof must_change_password === 'boolean') {
        state.must_change_password = must_change_password;
        try {
          localStorage.setItem('must_change_password', String(must_change_password));
        } catch (e) {
          // ignore
        }
        if (must_change_password === true) {
          state.show_password_step = true;
          try {
            localStorage.setItem('show_password_step', 'true');
          } catch (err) {
            // ignore
          }
        }
      }
      if (typeof show_password_step === 'boolean') {
        state.show_password_step = show_password_step;
        try {
          localStorage.setItem('show_password_step', String(show_password_step));
        } catch (e) {
          // ignore
        }
      }
      if (typeof medical_questionnaire_completed === 'boolean') {
        state.medical_questionnaire_completed = medical_questionnaire_completed;
        try {
          localStorage.setItem(
            'medical_questionnaire_completed',
            String(medical_questionnaire_completed)
          );
        } catch (e) {
          // ignore
        }
      }
      if (
        number_of_questions_filled !== null &&
        number_of_questions_filled !== undefined &&
        Number.isFinite(Number(number_of_questions_filled))
      ) {
        const n = Number(number_of_questions_filled);
        state.number_of_questions_filled = n;
        try {
          localStorage.setItem('number_of_questions_filled', String(n));
        } catch (e) {
          // ignore
        }
      }
      if (
        questionnaire_completion_percentage !== null &&
        questionnaire_completion_percentage !== undefined &&
        Number.isFinite(Number(questionnaire_completion_percentage))
      ) {
        const p = Math.min(
          100,
          Math.max(0, Math.round(Number(questionnaire_completion_percentage)))
        );
        state.questionnaire_completion_percentage = p;
        try {
          localStorage.setItem('questionnaire_completion_percentage', String(p));
        } catch (e) {
          // ignore
        }
      }
      if (
        totalSections !== null &&
        totalSections !== undefined &&
        Number.isFinite(Number(totalSections))
      ) {
        state.totalSections = Number(totalSections);
      }
      if (
        totalCompletedSections !== null &&
        totalCompletedSections !== undefined &&
        Number.isFinite(Number(totalCompletedSections))
      ) {
        state.totalCompletedSections = Number(totalCompletedSections);
      }
      if (
        totalIncompleteSections !== null &&
        totalIncompleteSections !== undefined &&
        Number.isFinite(Number(totalIncompleteSections))
      ) {
        state.totalIncompleteSections = Number(totalIncompleteSections);
      }
      if (
        completedSectionsPercentage !== null &&
        completedSectionsPercentage !== undefined &&
        Number.isFinite(Number(completedSectionsPercentage))
      ) {
        state.completedSectionsPercentage = Number(completedSectionsPercentage);
      }
      if (
        section_progress !== null &&
        section_progress !== undefined &&
        Number.isFinite(Number(section_progress))
      ) {
        state.section_progress = Math.min(
          100,
          Math.max(0, Math.round(Number(section_progress)))
        );
      }
      if (
        sections_completed !== null &&
        sections_completed !== undefined &&
        Number.isFinite(Number(sections_completed))
      ) {
        state.sections_completed = Math.max(0, Math.round(Number(sections_completed)));
      }
      if (overallStatus !== null && overallStatus !== undefined) {
        state.overallStatus =
          String(overallStatus).trim() !== '' ? String(overallStatus).trim() : null;
      }
    },
    updateField(state, action) {
      const { field, value } = action.payload;
      if (field in state) {
        state[field] = value;
      }
    },
    setViewAsRole(state, action) {
      const role = action.payload; // 'Patient' | 'Doctor/Nurse'
      if (state.role !== 'Supervisor' || (role !== 'Patient' && role !== 'Doctor/Nurse')) return;
      state.viewAsRole = role;
      try {
        localStorage.setItem('supervisorViewAsRole', role);
      } catch (e) {
        // ignore
      }
    },
    resetProfile() {
      return initialState;
    },
  },
});

export const { setProfile, updateField, setViewAsRole, resetProfile } = userSlice.actions;
export default userSlice.reducer;
